import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowTestingApiService } from '../services/workflow-testing-api.service';
import { WorkflowApiService } from '../services/workflow-api.service';
import { WorkflowBuilderApiService } from '../services/workflow-builder-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import {
  WorkflowActivationChecklistResponse,
  WorkflowChecklistItem,
  WorkflowTestRunResponse,
  WorkflowTestRunStep,
  WorkflowSimulateResponse,
  WorkflowSimulateStep,
  WorkflowAudienceEstimateResponse,
  WorkflowEmailTestResponse,
  WorkflowExecutionRunResponse
} from '../models/workflow-testing.model';
import { WorkflowResponse, WORKFLOW_OBJECT_TYPES_ENABLED, WORKFLOW_OBJECT_TYPE_LABELS } from '../models/workflow.model';
import {
  workflowNodeSubtypeLabel,
  workflowNodeTypeLabel,
  workflowObjectTypeLabel,
  workflowRecordLabel,
  workflowRunTypeLabel,
  workflowStatusLabel
} from '../models/workflow-labels';
import { branchKeyLabel } from '../workflow-builder/models/node-catalog';
import { WorkflowNodeResponse } from '../models/workflow-builder.model';
import { EntitySelectComponent } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';

@Component({
  selector: 'app-workflow-testing',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent],
  templateUrl: './workflow-testing.component.html'
})
export class WorkflowTestingComponent implements OnInit, OnDestroy {
  workflowId!: number;
  workflow: WorkflowResponse | null = null;

  // ── Nodos del workflow ────────────────────────────────────────────────────
  allNodes: WorkflowNodeResponse[] = [];
  selectableNodes: WorkflowNodeResponse[] = [];
  emailNodes: WorkflowNodeResponse[] = [];
  nodesLoading = false;

  // ── Shared form ───────────────────────────────────────────────────────────
  sharedObjectType = 'CONTACT';
  sharedObjectId: number | null = null;

  readonly objectTypeOptions = WORKFLOW_OBJECT_TYPES_ENABLED.map(t => ({
    value: t, label: WORKFLOW_OBJECT_TYPE_LABELS[t]
  }));

  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). El simulador y
   * el test run devuelven `nodeType`, `actionType`, `status` y `runType` en crudo (el motor no
   * traduce en estos endpoints), así que la pantalla pintaba `SEND_INTERNAL_NOTIFICATION`,
   * `PRODUCTION` y `SUCCESS`.
   */
  readonly nodeTypeText = workflowNodeTypeLabel;
  readonly nodeSubtypeText = workflowNodeSubtypeLabel;
  readonly statusText = workflowStatusLabel;
  readonly runTypeText = workflowRunTypeLabel;
  readonly objectTypeText = workflowObjectTypeLabel;
  readonly recordText = workflowRecordLabel;

  branchLabel(key: string | null | undefined): string {
    return key ? branchKeyLabel(key) : '—';
  }

  // ── Checklist ─────────────────────────────────────────────────────────────
  checklist: WorkflowActivationChecklistResponse | null = null;
  checklistLoading = false;
  checklistError = '';

  // ── Simulación ────────────────────────────────────────────────────────────
  simStartNodeId: number | null = null;
  simResult: WorkflowSimulateResponse | null = null;
  simLoading = false;
  simError = '';

  // ── Test run ──────────────────────────────────────────────────────────────
  testStartNodeId: number | null = null;
  testResult: WorkflowTestRunResponse | null = null;
  testLoading = false;
  testError = '';
  expandedSteps = new Set<number>();

  // ── Email test ────────────────────────────────────────────────────────────
  emailNodeId: number | null = null;
  emailTo = '';
  emailObjectType = 'CONTACT';
  emailObjectId: number | null = null;
  emailResult: WorkflowEmailTestResponse | null = null;
  emailLoading = false;
  emailError = '';

  // ── Audience estimate ─────────────────────────────────────────────────────
  audienceLimit: number | null = null;
  audienceResult: WorkflowAudienceEstimateResponse | null = null;
  audienceLoading = false;
  audienceError = '';

  // ── Execution runs ────────────────────────────────────────────────────────
  runsFilter: '' | 'TEST' | 'PRODUCTION' = 'TEST';
  runs: WorkflowExecutionRunResponse[] = [];
  runsLoading = false;
  runsError = '';

  private destroy$ = new Subject<void>();

  // ── Selector de registro por nombre (inc 10) ──────────────────────────────
  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  constructor(
    private route: ActivatedRoute,
    private testingApi: WorkflowTestingApiService,
    private workflowApi: WorkflowApiService,
    private builderApi: WorkflowBuilderApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService
  ) {}

  ngOnInit(): void {
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.sharedObjectType,
      () => this.authService.getCurrentUser()?.businessId || 1
    );
    const id = this.route.snapshot.paramMap.get('id');
    this.workflowId = Number(id);
    this.workflowApi.getById(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (wf) => {
        this.workflow = wf;
        this.sharedObjectType = wf.objectType;
        this.emailObjectType = wf.objectType;
      },
      error: () => {}
    });
    this.loadNodes();
  }

  onSharedObjectTypeChange(): void {
    this.sharedObjectId = null;
    this.recordDisplayText = '';
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Nodos ─────────────────────────────────────────────────────────────────

  loadNodes(): void {
    if (this.nodesLoading) return;
    this.nodesLoading = true;
    this.builderApi.getGraph(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (graph) => {
        this.allNodes = graph.nodes;
        this.selectableNodes = graph.nodes.filter(
          n => n.isActive && n.nodeType !== 'TRIGGER' && n.nodeType !== 'END' && n.nodeType !== 'ANNOTATION'
        );
        this.emailNodes = graph.nodes.filter(
          n => n.isActive && (
            n.nodeSubtype?.toUpperCase().includes('EMAIL') ||
            n.config?.['actionType']?.toString().toUpperCase().includes('EMAIL')
          )
        );
        this.nodesLoading = false;
      },
      error: () => { this.nodesLoading = false; }
    });
  }

  getNodeDisplayLabel(node: WorkflowNodeResponse): string {
    const typeLabel = workflowNodeTypeLabel(node.nodeType, node.nodeType);
    const title = node.title || (node.nodeSubtype ? workflowNodeSubtypeLabel(node.nodeSubtype) : '') || typeLabel;
    return `${title} (${typeLabel})`;
  }

  /** Nombre del nodo por su id: el simulador solo devuelve enteros de `workflow_node`. */
  getSimNodeName(nodeId: number | null | undefined): string {
    if (nodeId === null || nodeId === undefined) return '—';
    const node = this.getNodeById(nodeId ?? null);
    if (node?.title) return node.title;
    if (node) return workflowNodeSubtypeLabel(node.nodeSubtype, workflowNodeTypeLabel(node.nodeType, 'Nodo #' + nodeId));
    return 'Nodo #' + nodeId;
  }

  getNodeById(id: number | null): WorkflowNodeResponse | undefined {
    return id ? this.allNodes.find(n => n.id === id) : undefined;
  }

  // ── Checklist ─────────────────────────────────────────────────────────────

  runChecklist(): void {
    this.checklistLoading = true;
    this.checklistError = '';
    this.testingApi.getActivationChecklist(this.workflowId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.checklist = res; this.checklistLoading = false; },
      error: (err) => {
        this.checklistError = this.extractMessage(err, 'No se pudo ejecutar el checklist.');
        this.checklistLoading = false;
      }
    });
  }

  getChecklistItems(status: WorkflowChecklistItem['status']): WorkflowChecklistItem[] {
    return this.checklist?.items.filter(i => i.status === status) ?? [];
  }

  getChecklistSummary(): string {
    if (!this.checklist) return '';
    const passed   = this.getChecklistItems('PASSED').length;
    const warnings = this.getChecklistItems('WARNING').length;
    const failed   = this.getChecklistItems('FAILED').length;
    const parts: string[] = [];
    if (passed > 0)   parts.push(`${passed} correctos`);
    if (warnings > 0) parts.push(`${warnings} advertencia${warnings > 1 ? 's' : ''}`);
    if (failed > 0)   parts.push(`${failed} error${failed > 1 ? 'es' : ''}`);
    return parts.join(' · ');
  }

  checklistItemIcon(status: WorkflowChecklistItem['status']): string {
    return { PASSED: '✅', FAILED: '❌', WARNING: '⚠️', SKIPPED: '⏭️' }[status] ?? '•';
  }

  checklistItemClass(status: WorkflowChecklistItem['status']): string {
    switch (status) {
      case 'PASSED':  return 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'FAILED':  return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'WARNING': return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20';
      case 'SKIPPED': return 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/30';
      default:        return 'text-gray-600 bg-gray-50';
    }
  }

  // ── Simulación ────────────────────────────────────────────────────────────

  runSimulation(): void {
    if (!this.sharedObjectId) { this.simError = 'El ID de objeto es requerido.'; return; }
    this.simLoading = true;
    this.simError = '';
    this.simResult = null;
    this.testingApi.simulateWorkflow(this.workflowId, {
      objectType: this.sharedObjectType,
      objectId: this.sharedObjectId,
      startNodeId: this.simStartNodeId || null
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.simResult = res; this.simLoading = false; },
      error: (err) => { this.simError = this.getTestingErrorMessage(err); this.simLoading = false; }
    });
  }

  getSimStepIcon(step: WorkflowSimulateStep): string {
    switch (step.nodeType?.toUpperCase()) {
      case 'ACTION':    return '⚡';
      case 'CONDITION': return '🔀';
      case 'WAIT':      return '⏳';
      case 'TRIGGER':   return '🎯';
      case 'END':       return '🏁';
      default:          return '▶';
    }
  }

  getSimStepLabel(step: WorkflowSimulateStep): string {
    const node = this.getNodeById(step.nodeId);
    const type = workflowNodeTypeLabel(step.nodeType, 'Paso');
    const title = node?.title ?? (step.actionType ? workflowNodeSubtypeLabel(step.actionType) : '');
    return title ? `${type}: ${title}` : type;
  }

  // ── Test run ──────────────────────────────────────────────────────────────

  runTest(): void {
    if (!this.sharedObjectId) { this.testError = 'El ID de objeto es requerido.'; return; }
    this.testLoading = true;
    this.testError = '';
    this.testResult = null;
    this.expandedSteps.clear();
    this.testingApi.runWorkflowTest(this.workflowId, {
      objectType: this.sharedObjectType,
      objectId: this.sharedObjectId,
      startNodeId: this.testStartNodeId || null,
      executeSideEffects: false
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.testResult = res; this.testLoading = false; },
      error: (err) => { this.testError = this.getTestingErrorMessage(err); this.testLoading = false; }
    });
  }

  getTestSummary(): string {
    if (!this.testResult?.steps?.length) return '';
    const total   = this.testResult.steps.length;
    const skipped = this.testResult.steps.filter(s => s.status?.toUpperCase() === 'SKIPPED').length;
    const failed  = this.testResult.steps.filter(s => s.status?.toUpperCase() === 'FAILED').length;
    const parts: string[] = [`${total} paso${total > 1 ? 's' : ''} evaluados`];
    if (skipped > 0) parts.push(`${skipped} omitidos por seguridad`);
    if (failed > 0)  parts.push(`${failed} con error`);
    return parts.join(' · ');
  }

  toggleStepExpand(index: number): void {
    if (this.expandedSteps.has(index)) this.expandedSteps.delete(index);
    else this.expandedSteps.add(index);
  }

  getTestStepStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'FAILED':  return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'SKIPPED': return 'bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400';
      default:        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
  }

  isTestModeSideEffect(step: WorkflowTestRunStep): boolean {
    return step.reason === 'TEST_MODE_SIDE_EFFECTS_DISABLED' || step.status?.toUpperCase() === 'SKIPPED';
  }

  hasStepOutput(step: WorkflowTestRunStep): boolean {
    return !!(step.output && Object.keys(step.output).length > 0) || !!step.error;
  }

  getStepNodeLabel(step: WorkflowTestRunStep): string {
    const node = this.getNodeById(step.nodeId);
    if (node?.title) return node.title;
    if (step.actionType) return workflowNodeSubtypeLabel(step.actionType);
    return workflowNodeTypeLabel(step.nodeType, `Nodo #${step.nodeId}`);
  }

  // ── Email test ────────────────────────────────────────────────────────────

  sendEmailTest(): void {
    if (!this.emailNodeId)     { this.emailError = 'Selecciona el nodo de email a probar.'; return; }
    if (!this.sharedObjectId)  { this.emailError = 'Selecciona el registro de prueba compartido arriba.'; return; }
    if (!this.emailTo.trim())  { this.emailError = 'El destinatario es requerido.'; return; }
    if (!this.isValidEmail(this.emailTo)) { this.emailError = 'El formato del email no es válido.'; return; }

    this.emailLoading = true;
    this.emailError = '';
    this.emailResult = null;
    this.testingApi.sendWorkflowTestEmail({
      workflowId: this.workflowId,
      nodeId: this.emailNodeId,
      objectType: this.sharedObjectType,
      objectId: this.sharedObjectId,
      to: this.emailTo.trim()
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.emailResult = res; this.emailLoading = false; },
      error: (err) => { this.emailError = this.getTestingErrorMessage(err); this.emailLoading = false; }
    });
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // ── Audience ──────────────────────────────────────────────────────────────

  estimateAudience(): void {
    this.audienceLoading = true;
    this.audienceError = '';
    this.audienceResult = null;
    this.testingApi.estimateAudience(this.workflowId, {
      limitPreview: this.audienceLimit || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.audienceResult = res; this.audienceLoading = false; },
      error: (err) => { this.audienceError = this.getTestingErrorMessage(err); this.audienceLoading = false; }
    });
  }

  // ── Runs ──────────────────────────────────────────────────────────────────

  loadRuns(): void {
    this.runsLoading = true;
    this.runsError = '';
    this.testingApi.getWorkflowExecutionRuns({
      workflowId: this.workflowId,
      runType: this.runsFilter || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.runs = res; this.runsLoading = false; },
      error: (err) => { this.runsError = this.extractMessage(err, 'No se pudieron cargar los runs.'); this.runsLoading = false; }
    });
  }

  getRunStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'bg-green-100 text-green-700';
      case 'FAILED':    return 'bg-red-100 text-red-700';
      case 'RUNNING':   return 'bg-blue-100 text-blue-700';
      default:          return 'bg-gray-100 text-gray-500';
    }
  }

  getRunTypeBadgeClass(type: string): string {
    return type === 'TEST'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
  }

  // ── Error helpers ─────────────────────────────────────────────────────────

  getTestingErrorMessage(err: unknown): string {
    const e = err as any;
    const body = e?.error ?? {};
    const code: string =
      body?.code ?? body?.errorCode ?? body?.details?.code ?? body?.details?.errorCode ??
      (typeof body?.details === 'string' ? body.details : '') ?? e?.code ?? e?.errorCode ?? '';

    switch (code) {
      case 'WORKFLOW_ACTIVATION_CHECKLIST_FAILED':
        return 'No se puede activar el workflow porque falló la validación pre-activación.';
      case 'WORKFLOW_TEST_SIDE_EFFECTS_NOT_ALLOWED':
        return 'Las acciones reales no están permitidas en modo prueba.';
      case 'WORKFLOW_SIMULATION_FAILED':
        return 'No se pudo simular el workflow.';
      case 'WORKFLOW_EMAIL_TEST_INVALID_RECIPIENT':
        return 'La dirección de correo de prueba no es válida.';
      case 'WORKFLOW_AUDIENCE_ESTIMATE_UNSUPPORTED_TRIGGER':
        return 'La estimación de audiencia no está soportada para este tipo de disparador.';
      case 'WORKFLOW_RAMP_UP_INVALID_PERCENTAGE':
        return 'El porcentaje de activación gradual debe estar entre 1 y 100.';
    }
    // Traducción de mensajes técnicos del backend a lenguaje accionable.
    const raw = this.extractMessage(err, '').toLowerCase();
    if (raw.includes('trigger inicial') || (raw.includes('resolver') && raw.includes('trigger'))) {
      return 'Este registro no cumple el disparador inicial del workflow. Elige un registro que coincida con el disparador, o revisa la configuración del trigger.';
    }
    if ((raw.includes('crm_event') || raw.includes('crm event')) || (raw.includes('trigger') && raw.includes('no se soporta'))) {
      return 'Este disparador (evento CRM) requiere un evento real y no puede simularse aquí. Usa el "Probador de disparadores" para enviar un evento de prueba.';
    }
    return this.extractMessage(err, 'Ocurrió un error inesperado.');
  }

  private extractMessage(err: unknown, fallback: string): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? fallback;
  }
}
